import { useState, useEffect } from 'react';
import { BuildInfo } from '../types';
import { apiService } from '../services/api';

interface UseBuildInfoResult {
  buildInfo: BuildInfo | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

// Cache the build info globally since it doesn't change often
// Include API URL in cache key to prevent cross-environment caching
let cachedBuildInfo: BuildInfo | null = null;
let cacheTimestamp: number = 0;
let cacheApiUrl: string = '';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export function useBuildInfo(): UseBuildInfoResult {
  const [buildInfo, setBuildInfo] = useState<BuildInfo | null>(cachedBuildInfo);
  const [loading, setLoading] = useState(!cachedBuildInfo);
  const [error, setError] = useState<string | null>(null);

  const fetchBuildInfo = async () => {
    // Check if we have fresh cached data for the same API URL
    const now = Date.now();
    const currentApiUrl = import.meta.env.VITE_API_URL || '';
    
    if (cachedBuildInfo && 
        cacheApiUrl === currentApiUrl && 
        (now - cacheTimestamp) < CACHE_DURATION) {
      setBuildInfo(cachedBuildInfo);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const info = await apiService.getBuildInfo();
      
      // Update cache with API URL
      cachedBuildInfo = info;
      cacheTimestamp = now;
      cacheApiUrl = currentApiUrl;
      
      setBuildInfo(info);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch build info';
      setError(errorMessage);
      
      // If we have cached data, keep using it despite the error
      if (!cachedBuildInfo) {
        setBuildInfo(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const refetch = () => {
    // Clear cache to force fresh fetch
    cachedBuildInfo = null;
    cacheTimestamp = 0;
    cacheApiUrl = '';
    fetchBuildInfo();
  };

  useEffect(() => {
    fetchBuildInfo();
  }, []);

  return { buildInfo, loading, error, refetch };
}